import React from 'react'
import {
    Checkbox,
    CircularProgress,
    ListItemText,
    InputAdornment,
    MenuItem,
    TextField,
    TextFieldProps,
} from '@mui/material'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'
import { optionsToEntries } from './utils'
import type { LoadingProps, OptionsProps } from './types'

type Props<FormValues extends FieldValues = FieldValues> = UseControllerProps<FormValues> &
    TextFieldProps &
    OptionsProps &
    LoadingProps & {
        native?: boolean
    } & {
        /** Creates multiple select checkbox. Defaults native to false and SelectProps.multiple to true */
        multiple?: boolean
        /** If multiple, value is an array. This converts value to comma seperated string for react hook form */
        valueAsString?: boolean
    }

const LoadingInputAdornment: React.VFC = () => (
    <InputAdornment position="end">
        <CircularProgress size={20} sx={{ mr: '20px' }} />
    </InputAdornment>
)

export const MuiRhfCheckboxSelectFieldInputRef = <FormValues extends FieldValues = FieldValues>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
    options = [],
    loading,
    native,
    multiple,
    valueAsString,
    ...textFieldProps
}: Props<FormValues>): JSX.Element => {
    const {
        field: { ref, value, ...rhfFieldProps },
        fieldState: { error },
    } = useController<FormValues>({ rules, shouldUnregister, defaultValue, name })

    const optionEntries = optionsToEntries(options)
    const errorMessage = error instanceof Error ? error.message : undefined

    const nativeSelect =
        native == null
            ? textFieldProps.SelectProps?.native == null
                ? !multiple
                : textFieldProps.SelectProps?.native
            : native
    const multipleSelect =
        textFieldProps.SelectProps?.multiple == null ? multiple || false : textFieldProps.SelectProps?.multiple
    const emptyValue = multipleSelect ? [] : ''
    const valueOrEmptyValue = (value || emptyValue) as string | string[]
    const valueAsArrayIfMultiple =
        typeof valueOrEmptyValue === 'string' ? valueOrEmptyValue.split(',') : valueOrEmptyValue

    return (
        <TextField
            inputRef={ref}
            {...rhfFieldProps}
            {...textFieldProps}
            onChange={(e) => {
                const v = e.target.value
                const maybeStringValue = valueAsString === true ? v.toString() : valueOrEmptyValue

                rhfFieldProps.onChange(maybeStringValue)
                textFieldProps.onChange?.(e)
                if (multipleSelect) {
                    rhfFieldProps.onBlur()
                }
            }}
            onBlur={(e) => {
                rhfFieldProps.onBlur()
                textFieldProps.onBlur?.(e)
            }}
            InputProps={{
                endAdornment: loading && <LoadingInputAdornment />,
                ...textFieldProps.InputProps,
            }}
            select
            /** Value is an array for multiple select */
            value={valueAsArrayIfMultiple}
            required={textFieldProps.required || !!rules?.required}
            error={textFieldProps.error || !!error}
            helperText={textFieldProps.helperText || errorMessage}
            SelectProps={{
                native: nativeSelect,
                multiple: multipleSelect,
                renderValue: (selected) =>
                    Array.isArray(selected) ? selected.join(',') : (selected as any).toString(),
            }}
        >
            {optionEntries.map(([optionValue, optionLabel, optionProps]) => {
                const checked =
                    Array.isArray(valueAsArrayIfMultiple) &&
                    (valueAsArrayIfMultiple as (typeof optionValue)[]).includes(optionValue)

                return nativeSelect ? (
                    optionValue ? (
                        <option
                            key={String(optionValue)}
                            // @ts-expect-error allow boolean optionValue
                            value={optionValue}
                            {...optionProps}
                        >
                            {String(optionLabel)}
                        </option>
                    ) : (
                        <option
                            key={String(optionValue)}
                            // @ts-expect-error allow boolean optionValue
                            value={optionValue}
                            {...optionProps}
                        />
                    )
                ) : (
                    // @ts-expect-error allow boolean optionValue
                    <MenuItem key={String(optionValue)} value={optionValue} {...optionProps}>
                        {multipleSelect ? (
                            <>
                                <Checkbox checked={checked} />
                                <ListItemText primary={optionLabel} />
                            </>
                        ) : (
                            String(optionLabel)
                        )}
                    </MenuItem>
                )
            })}
        </TextField>
    )
}

export default MuiRhfCheckboxSelectFieldInputRef
export {
    MuiRhfCheckboxSelectFieldInputRef as MuiRhfCheckboxSelectField,
    MuiRhfCheckboxSelectFieldInputRef as MuiRhfCheckboxSelect,
    MuiRhfCheckboxSelectFieldInputRef as CheckboxSelectField,
    MuiRhfCheckboxSelectFieldInputRef as CheckboxSelect,
}
export type { Props as MuiRhfCheckboxSelectFieldProps, Props as CheckboxSelectFieldProps }
